import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useMediaQuery } from 'design-system/my-lib';
import ContextListPage from '../ContextsPage/ContextListPage';
import ContextEditorPage from '../ContextEditorPage/ContextEditorPage';

interface LegacyContextRedirectProps {
  /**
   * Builds the `/studio/context*` destination path from the route params matched on the legacy
   * `/contexts*` pattern. Query string is appended by this component (SC3) — mirrors
   * `LegacyComposeRedirect`.
   */
  toPathFromParams: (params: Readonly<Record<string, string | undefined>>) => string;
  /**
   * Mobile "read-and-clone" alias (SC3): below `StudioShell`'s own `(max-width: 1023px)`
   * breakpoint, `/contexts` and `/contexts/:systemName/edit` keep working — the library
   * (chrome shown, unlike the Studio host) and a read-only editor with clone. `undefined` means
   * this legacy route has no mobile alias (`/contexts/new`, `/contexts/new/blank`) and always
   * redirects, on every viewport, so no phone-usable capability besides list/read/clone is lost
   * (nothing existed to lose for a chooser route the Studio "New…" menu replaces).
   */
  mobileAlias?: 'list' | 'editor';
}

/** `/contexts*` → `/studio/context*` on desktop, preserving `location.search` (EAINT-862).
 * Below-desktop `/contexts` and `/contexts/:systemName/edit` render the mobile alias instead. */
const LegacyContextRedirect: React.FC<LegacyContextRedirectProps> = ({
  toPathFromParams,
  mobileAlias,
}) => {
  const params = useParams();
  const location = useLocation();
  const belowDesktop = useMediaQuery('(max-width: 1023px)');

  if (belowDesktop && mobileAlias === 'list') {
    return <ContextListPage />;
  }
  if (belowDesktop && mobileAlias === 'editor') {
    return <ContextEditorPage readOnly />;
  }

  const to = `${toPathFromParams(params)}${location.search}`;
  // entry: 'redirect' mirrors LegacyComposeRedirect — tells anything reading the landing state
  // this came from a legacy /contexts* URL, not a nav click or a typed/bookmarked address.
  return <Navigate to={to} replace state={{ entry: 'redirect' }} />;
};

export default LegacyContextRedirect;
